/**
 * Experience Section Component
 * Migrated to TypeScript with subcomponents
 */

import React, { useEffect, useRef, useState } from 'react';
import { FaMapMarkerAlt, FaCalendarAlt, FaCode } from 'react-icons/fa';
import { SiCsharp, SiXamarin, SiAzuredevops, SiJenkins, SiAppium, SiApachemaven, SiMqtt, SiAndroidstudio, SiPython, SiPytorch } from 'react-icons/si';
import { FaJava } from 'react-icons/fa';
import { GiBrain, GiEyeTarget, GiProcessor } from 'react-icons/gi';
import { experiences } from '../../data/experiences';
import { useColorScheme } from '../../contexts/ColorSchemeContext';

const Experience: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const { getColorClasses } = useColorScheme();
  const colors = getColorClasses();

  const getTechIcon = (techName: string) => {
    const iconMap: Record<string, any> = {
      'C#': SiCsharp,
      'XAML': FaCode,
      'Xamarin': SiXamarin,
      'Azure DevOps': SiAzuredevops,
      'Jenkins': SiJenkins,
      'Appium': SiAppium,
      'Java': FaJava,
      'Maven': SiApachemaven,
      'MQTT': SiMqtt,
      'PowerShell': FaCode,
      'Android': SiAndroidstudio,
      'Python': SiPython,
      'PyTorch': SiPytorch,
      'CUDA': GiProcessor,
      'YOLO-V11': GiEyeTarget,
      'ResNet34': GiBrain,
      'ViT': GiBrain,
      'Computer Vision': GiEyeTarget,
      'Deep Learning': GiBrain
    };
    return iconMap[techName] || FaCode;
  };

  const getTechColor = (techName: string) => {
    const colorMap: Record<string, string> = {
      'C#': '#239120',
      'Java': '#007396',
      'Xamarin': '#3498DB',
      'Azure DevOps': '#0078D7',
      'Jenkins': '#D24939',
      'Appium': '#41BDF5',
      'Maven': '#C71A36',
      'MQTT': '#660066',
      'PowerShell': '#012456',
      'Android Studio': '#3DDC84',
      'Robot Framework': '#000000',
      'ASP.NET': '#512BD4',
      'XAML': '#0C54C2',
      'Android': '#3DDC84',
      'Python': '#3776AB',
      'PyTorch': '#EE4C2C',
      'CUDA': '#76B900',
      'YOLO-V11': '#00FFFF',
      'ResNet34': '#FF6B6B',
      'ViT': '#4ECDC4',
      'Computer Vision': '#00BFFF',
      'Deep Learning': '#4ECDC4'
    };
    return colorMap[techName] || '#6C757D';
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="experience" ref={sectionRef} className="py-20">
      <div className="container mx-auto px-4">
        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="accent-gradient-text">
                Work Experience
              </span>
            </h2>
            <p className={`text-xl ${colors.textSecondary} max-w-3xl mx-auto`}>
              Professional journey in software development and engineering
            </p>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b accent-gradient-bg rounded-full hidden lg:block"></div>

            <div className="space-y-12">
              {experiences.map((exp, index) => (
                <div
                  key={exp.id}
                  className={`flex items-center ${
                    index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  }`}
                  style={{ animationDelay: `${index * 0.2}s` }}
                >
                  {/* Timeline Dot */}
                  <div className={`hidden lg:flex absolute left-1/2 transform -translate-x-1/2 w-4 h-4 ${colors.bg} rounded-full border-4 border-white dark:border-gray-900 z-10`}></div>

                  {/* Content Card */}
                  <div className={`w-full lg:w-5/12 ${
                    index % 2 === 0 ? 'lg:pr-8' : 'lg:pl-8'
                  }`}>
                    <div className={`${colors.cardBackground} rounded-2xl p-8 shadow-lg border ${colors.borderColor} hover:shadow-xl transition-all duration-300 transform hover:scale-105`}>
                      {/* Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className={`text-2xl font-bold ${colors.textPrimary} mb-2`}>
                            {exp.title}
                          </h3>
                          <div className={`flex items-center gap-3 text-lg font-semibold ${colors.text} mb-2`}>
                            {exp.companyLogo && (
                              <div className="w-8 h-8 flex-shrink-0">
                                {React.createElement(exp.companyLogo, { className: "w-full h-full" })}
                              </div>
                            )}
                            <span>{exp.company}</span>
                          </div>
                          <div className={`flex items-center ${colors.textSecondary} mb-1`}>
                            <FaMapMarkerAlt className="w-4 h-4 mr-2" />
                            {exp.location}
                          </div>
                          <div className={`flex items-center ${colors.textSecondary} mb-4`}>
                            <FaCalendarAlt className="w-4 h-4 mr-2" />
                            {exp.date}
                          </div>
                          {exp.project && (
                            <div className="mb-4">
                              <span className="inline-flex items-center px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 text-sm font-medium rounded-full">
                                {exp.projectLogo && (
                                  <div className="w-4 h-4 mr-2 flex-shrink-0">
                                    {React.createElement(exp.projectLogo, { className: "w-full h-full" })}
                                  </div>
                                )}
                                {exp.project}
                              </span>
                            </div>
                          )}
                        </div>
                        <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 text-sm font-medium rounded-full">
                          {exp.type}
                        </span>
                      </div>

                      {/* Achievements */}
                      <div className="mb-6">
                        <h4 className={`text-lg font-semibold ${colors.textPrimary} mb-3`}>
                          Key Achievements
                        </h4>
                        <ul className="space-y-2">
                          {exp.achievements.map((achievement, i) => (
                            <li key={i} className={`flex items-start ${colors.textSecondary}`}>
                              <span className={`${colors.text} mr-3 mt-1`}>•</span>
                              <span>{achievement}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Technologies */}
                      <div>
                        <h4 className={`text-lg font-semibold ${colors.textPrimary} mb-3 flex items-center`}>
                          <FaCode className="w-5 h-5 mr-2" />
                          Technologies Used
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {exp.technologies.map((tech, techIndex) => {
                            const Icon = getTechIcon(tech);
                            return (
                              <div
                                key={techIndex}
                                className={`flex items-center space-x-2 px-3 py-1 rounded-full ${colors.background} border border-gray-300 dark:border-gray-600 shadow-lg hover:shadow-xl transition-all duration-300`}
                              >
                                <Icon 
                                  className="w-4 h-4 dark:[filter:drop-shadow(0_0_0.5px_rgba(255,255,255,0.5))]" 
                                  style={{ color: getTechColor(tech) }}
                                />
                                <span className={`text-sm ${colors.textPrimary}`}>{tech}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;

